import { NgxsDataPluginModule } from '@angular-ru/ngxs';
import { provideHttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject, isDevMode, Injector } from '@angular/core';
import { getAnalytics, provideAnalytics, ScreenTrackingService, UserTrackingService } from '@angular/fire/analytics';
import { FirebaseApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { provideAppCheck } from '@angular/fire/app-check';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { NbAuthModule, NbTokenLocalStorage, NbTokenStorage } from '@nebular/auth';
import { NbRoleProvider, NbSecurityModule } from '@nebular/security';
import { NbChatModule } from '@nebular/theme';
import { provideStore } from '@ngxs/store';
import { CapitalizePipe } from '@res/ng-capitalize';
import { AngularProjectContext, FirebaseConfigToken } from '@res/ng-data-access';
import {
  ALL_THEMES as ALL_THEMES_TOKEN,
  APP_CONFIG,
  CoreModule,
  FCMState,
  getAppCheck,
  NB_CORE_PROVIDERS,
  NbFirebasePasswordStrategy,
  provideCheckForUpdate,
  provideCookieConsent,
  RemoteConfigService,
  RemoteConfigState,
  TranslocoHttpLoader,
  UserState,
} from '@res/ngg-core';
import { ALL_THEMES, ThemeModule } from '@res/ngg-theme';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideEcharts } from 'ngx-echarts';
import { of } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { LosserState } from './data-access/store/losser/losser.state';
import { PartnerState } from './data-access/store/partner/partner.state';
import { RentState } from './data-access/store/rent/rent.state';
import { VehicleState } from './data-access/store/vehicle/vehicle.state';
import { RemoteConfigKey } from './remote-config.key';
export class NbSimpleRoleProvider extends NbRoleProvider {
  getRole() {
    // here you could provide any role based on any auth flow
    return of(['guest', 'customre', 'admin', 'support', 'francise partner']);
  }
}

// Bootstrap must not hang on Remote Config, and must not die with it. A rejected
// APP_INITIALIZER promise makes Angular abort bootstrap and leave a blank page, and
// the fetch has two independent failure shapes: it rejects (offline, the preview
// domain is not on the Firebase allow list, App Check refuses a token) and it stalls
// (the SDK waits out its own 60s fetch timeout, IndexedDB is unavailable in private
// mode). Catching alone still leaves a minute of white screen, so bound the wait as
// well. Whatever the fetch eventually does, it keeps resolving in the background.
const REMOTE_CONFIG_BOOTSTRAP_TIMEOUT_MS = 8000;

export function appInitializerFactory() {
  const configService = inject(RemoteConfigService);
  return () => {
    // The timer has to be cleared on the happy path. APP_INITIALIZER runs inside the
    // Angular zone, so zone.js counts a live setTimeout as a pending macrotask and
    // ApplicationRef.whenStable() would not resolve until it fired. This app registers
    // its service worker with the default 'registerWhenStable' strategy, so a leaked
    // timer would push ngsw-worker.js registration back by the full 8s on every load,
    // even when Remote Config came back in 200ms.
    let bootstrapTimer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<void>((resolve) => {
      bootstrapTimer = setTimeout(resolve, REMOTE_CONFIG_BOOTSTRAP_TIMEOUT_MS);
    });
    const loaded = configService
      .initiateConfig()
      .catch((error: unknown) => console.error('Remote Config failed to load; starting with defaults', error));

    return Promise.race([loaded, timeout]).finally(() => clearTimeout(bootstrapTimer));
  };
}

// const Stores = [RentStore, LosserStore, VehicleStore, PartnerStore, UserStore];
export const appConfig: ApplicationConfig = {
  providers: [
    { provide: APP_CONFIG, useValue: environment },
    { provide: ALL_THEMES_TOKEN, useValue: ALL_THEMES },
    { provide: FirebaseConfigToken, useValue: environment.firebaseConfig },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: environment.matOptions.formField },
    provideRouter(appRoutes, withRouterConfig(environment.routerConfigOptions)),
    provideAnimations(),
    provideAnimationsAsync(),
    provideNativeDateAdapter(),
    provideEcharts(),
    provideCharts(withDefaultRegisterables()),
    // ...Stores,
    // importProvidersFrom(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    // Resolve the FirebaseApp from the provider injector rather than letting getAppCheck fall back
    // to the default getApp(). AngularFire runs this factory outside the Angular zone and there is
    // no ordering guarantee against provideFirebaseApp above, so the fallback could run first and
    // abort startup with "No Firebase App '[DEFAULT]'". The CRM already does it this way.
    provideAppCheck((injector: Injector) =>
      getAppCheck(environment.firebaseAppCheckKey, true, 'Entrprise', injector.get(FirebaseApp)),
    ),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideAnalytics(() => getAnalytics()),
    provideFunctions(() => getFunctions()),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideStore(
      [RentState, PartnerState, VehicleState, LosserState, UserState, FCMState, RemoteConfigState],
      ...environment.ngxsPlugins,
    ),
    NbFirebasePasswordStrategy,
    importProvidersFrom([
      CoreModule.forRoot(),
      NbChatModule.forRoot(environment.nbChatConfig),
      NbAuthModule.forRoot({
        strategies: [NbFirebasePasswordStrategy.setup(environment.coreConfig.authOptions.firebaseStrategieSetup)],
        forms: environment.coreConfig.authOptions.forms,
      }),
      NbSecurityModule.forRoot(environment.coreConfig.securityOptions),
      NgxsDataPluginModule.forRoot(),
    ]),
    importProvidersFrom(ThemeModule.forRoot()),
    importProvidersFrom(InlineSVGModule.forRoot()),
    provideHttpClient(),
    { provide: NbRoleProvider, useClass: NbSimpleRoleProvider },
    { provide: NbTokenStorage, useClass: NbTokenLocalStorage },
    { provide: AngularProjectContext, useValue: { service: environment.serviceName, version: environment.version } },
    provideTransloco({ config: environment.transloco, loader: TranslocoHttpLoader }),
    NB_CORE_PROVIDERS,
    ScreenTrackingService,
    UserTrackingService,
    CapitalizePipe,
    TranslocoService,
    RemoteConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [RemoteConfigService],
      multi: true,
    },
    provideCookieConsent(RemoteConfigKey.cookieConsentConfig),
    // Exactly one call. `provideServiceWorker` is not additive: it provides the script under a
    // single-valued token, so a second call left both registrations pointing at
    // firebase-messaging-sw.js, ngsw-worker.js was never registered, and the offline cache plus
    // SwUpdate/CheckForUpdateService went with it. The messaging worker does not belong here
    // anyway: getToken() registers /firebase-messaging-sw.js under its own
    // /firebase-cloud-messaging-push-scope, which is the scope FCMState looks it up by.
    provideServiceWorker('ngsw-worker.js', { enabled: !isDevMode() }),
    // Constructs CheckForUpdateService. Listing the class here, as this array used to, only
    // registered a factory nobody called, so the update watcher never ran.
    provideCheckForUpdate(),
    // OriginHeaderRemoveInterceptor,
  ],
};
